import { call, cancelled, put, SagaReturnType, takeEvery, takeLatest } from 'redux-saga/effects';

import { callApi, type GeneratorResult } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import {
    getArticleCollections,
    invalidateUserCollectionsMetadata,
    removeArticleFromCollections,
    upsertArticleCollections,
} from '@pressreader/social-api';

import {
    loadArticleCollectionsAction,
    loadArticleCollectionsBegan,
    loadArticleCollectionsCancelled,
    loadArticleCollectionsDone,
    loadArticleCollectionsFailed,
    removeArticleFromCollectionsAction,
    removeArticleFromCollectionsBegan,
    removeArticleFromCollectionsDone,
    removeArticleFromCollectionsFailed,
    upsertArticleCollectionsAction,
    upsertArticleCollectionsBegan,
    upsertArticleCollectionsDone,
    upsertArticleCollectionsFailed,
} from '../../actions';

import { getOrWaitForArticleCollections, waitUntilArticleCollectionsLoaded } from './article.collections.utils';

function* loadArticleCollectionsById({ articleId, isForce = false }: { isForce?: boolean; articleId: string }) {
    const collectionsCached = yield* getOrWaitForArticleCollections(articleId);

    yield put(
        loadArticleCollectionsBegan({
            articleId,
        }),
    );

    const isCollectionsSetExist = Boolean(collectionsCached) && !isForce;
    const collections = isCollectionsSetExist ? collectionsCached : yield* callApi(getArticleCollections, articleId);

    yield put(
        loadArticleCollectionsDone({
            articleId,
            collections,
        }),
    );
}

function* handleLoadIssuePageSetByIdAction({ payload }: ReturnType<typeof loadArticleCollectionsAction>) {
    const { articleId, isForce } = payload;

    yield waitUntilArticleCollectionsLoaded(articleId);

    try {
        yield* loadArticleCollectionsById({
            articleId,
            isForce,
        });
    } catch (e) {
        error(e);
        yield put(loadArticleCollectionsFailed({ articleId, error: e }));
    } finally {
        const isCancelled: boolean = yield cancelled();
        if (isCancelled) {
            yield put(loadArticleCollectionsCancelled({ articleId }));
        }
    }
}

function* handleUpsertArticleCollectionsAction({
    payload: { articleId, collectionsData: articleCollectionsDto },
}: ReturnType<typeof upsertArticleCollectionsAction>): GeneratorResult<void> {
    try {
        yield* waitUntilArticleCollectionsLoaded(articleId);

        yield put(upsertArticleCollectionsBegan({ articleId }));

        const articleCollections: SagaReturnType<typeof upsertArticleCollections> = yield call(
            upsertArticleCollections,
            articleId,
            articleCollectionsDto,
        );
        // contentCount changed - drop the cached collections metadata and let subscribers refresh. AB#173760
        yield call(invalidateUserCollectionsMetadata);

        yield put(upsertArticleCollectionsDone({ articleId, articleCollections }));
    } catch (e) {
        error(e);
        yield put(upsertArticleCollectionsFailed({ articleId }));
    }
}

function* handleRemoveArticleFromCollectionsAction({ payload: { articleId } }: ReturnType<typeof removeArticleFromCollectionsAction>) {
    try {
        yield* waitUntilArticleCollectionsLoaded(articleId);

        yield put(removeArticleFromCollectionsBegan({ articleId }));

        yield call(removeArticleFromCollections, articleId);
        yield call(invalidateUserCollectionsMetadata);

        yield put(removeArticleFromCollectionsDone({ articleId }));
    } catch (err) {
        error(err);
        yield put(removeArticleFromCollectionsFailed({ articleId }));
    }
}

function* saga() {
    yield takeLatest(loadArticleCollectionsAction, handleLoadIssuePageSetByIdAction);
    yield takeEvery(upsertArticleCollectionsAction, handleUpsertArticleCollectionsAction);
    yield takeEvery(removeArticleFromCollectionsAction, handleRemoveArticleFromCollectionsAction);
}

export { saga };
